/**
 * RankingRow - Shared Component
 * 
 * Displays a single Pokémon ranking row with identity, moveset, and metrics.
 * Used in PvP rankings (desktop table view).
 * 
 * Design System: PvP Section - Ranking Display
 */

import React from 'react';
import { motion } from 'framer-motion';
import PokemonVariantDisplay from '../PokemonVariantDisplay';
import MovesetPanel from './MovesetPanel';
import TypeBadge from './TypeBadge';
import MetricDisplay from './MetricDisplay';
import { normalizePokemonData, normalizeMoveName } from '../../utils/pokemonDataUtils';
import { getPokemonImage } from '../../utils/pokemonImageResolver';

const RankingRow = ({
  pokemon,
  index = 0,
  language = 'th',
  getRankIcon = () => null,
  onRowClick
}) => {
  // Normalize Pokemon data
  const normalizedPokemon = normalizePokemonData(pokemon, language);
  
  // Get Pokemon name - use language-appropriate name
  const pokemonName = normalizedPokemon.pokemon_name || 
                      (language === 'th' ? normalizedPokemon.Pokemon_Name_TH : normalizedPokemon.Pokemon_Name_EN) ||
                      (language === 'th' ? normalizedPokemon.name_th : normalizedPokemon.name) || 
                      normalizedPokemon.name ||
                      'Unknown Pokemon';
  
  // Get types
  const types = normalizedPokemon.pokemon_types || [];
  
  // Get charged moves (keep English for type detection - MovesetPanel handles translation)
  const chargedMoves = [
    normalizeMoveName(normalizedPokemon.charged_move_1, 'en'),
    normalizeMoveName(normalizedPokemon.charged_move_2, 'en')
  ].filter(Boolean);
  
  // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
  const pokemonWithImage = {
    ...normalizedPokemon,
    image_url: getPokemonImage(normalizedPokemon, 'default')
  };

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.02 }}
      className="hover:bg-blue-50/30 transition-colors cursor-pointer"
      onClick={onRowClick}
    >
      {/* Pokemon Identity */}
      <td className="px-4 py-4">
        <div className="flex items-center space-x-3">
          <div className="relative flex-shrink-0">
            <PokemonVariantDisplay
              pokemon={pokemonWithImage}
              showBadges={true}
              showCssClasses={true}
              className="w-16 h-16"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-gray-900 mb-1.5 truncate">
              {pokemonName}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {types.map((type, idx) => (
                <TypeBadge key={idx} type={type} size="default" />
              ))}
            </div>
          </div>
        </div>
      </td>
      
      {/* Moveset Panel - HERO SECTION */}
      <td className="px-4 py-4">
        <MovesetPanel
          fastMove={normalizeMoveName(normalizedPokemon.fast_move, 'en')}
          chargedMoves={chargedMoves}
          variant="default"
          language={language}
        />
      </td>
      
      {/* Rank - First */}
      <td className="px-4 py-4 text-center">
        <div className="flex items-center justify-center space-x-1">
          {getRankIcon && getRankIcon(pokemon.rank || pokemon.rank_position)}
          <span className="text-sm font-medium text-gray-600">
            #{pokemon.rank || pokemon.rank_position}
          </span>
        </div>
      </td>
      
      {/* Score - Secondary */}
      <td className="px-4 py-4 text-center">
        <MetricDisplay
          value={parseFloat(pokemon.score || 0).toFixed(1)}
          variant="default"
          align="center"
          showLabel={false}
        />
      </td>
    </motion.tr>
  );
};

export default RankingRow;
